/**
 * Country timeline panel — temporal event view for a selected country.
 *
 * Listens for country:selected event. Shows all events matching
 * the country sorted by time, grouped into Today/Yesterday/This Week/Older.
 * Filter checkboxes for event types. Click → emit event:selected.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { SEVERITY_CSS_COLORS } from '@/utils/severity';
import type { NormalizedEvent, EventType, Severity } from '@/sources/base-source';

const TYPE_LABELS: Partial<Record<EventType, string>> = {
  conflict: 'Conflict',
  protest: 'Protest',
  disaster: 'Disaster',
  military: 'Military',
  cyber_incident: 'Cyber',
  infrastructure: 'Infrastructure',
  economic: 'Economic',
  political: 'Political',
  social: 'Social',
  fire: 'Fire',
  earthquake: 'Earthquake',
  weather: 'Weather',
  sanction: 'Sanction',
  diplomatic: 'Diplomatic',
};

export function createCountryTimelinePanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'country-timeline-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const headerArea = document.createElement('div');
  headerArea.style.cssText = 'padding: 12px; border-bottom: 1px solid var(--border-primary); flex-shrink: 0;';
  container.appendChild(headerArea);

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px; flex: 1; overflow-y: auto;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;
  let currentCountry: { name: string; code: string } | null = null;
  let hiddenTypes = new Set<EventType>();

  function scheduleRender(): void {
    if (renderPending || !currentCountry) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function getCountryEvents(): NormalizedEvent[] {
    if (!currentCountry) return [];
    const events = appStore.getState().events;
    const code = currentCountry.code.toUpperCase();
    const name = currentCountry.name.toUpperCase();
    return events.filter(e => {
      const cc = e.location?.countryCode?.toUpperCase();
      if (cc === code) return true;
      const cn = e.location?.country?.toUpperCase();
      if (cn === name) return true;
      return false;
    });
  }

  function getTimeGroup(timestamp: string): string {
    const now = new Date();
    const ts = new Date(timestamp);
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart.getTime() - 86400000);
    const weekStart = new Date(todayStart.getTime() - 6 * 86400000);

    if (ts >= todayStart) return 'Today';
    if (ts >= yesterdayStart) return 'Yesterday';
    if (ts >= weekStart) return 'This Week';
    return 'Older';
  }

  function render(): void {
    headerArea.textContent = '';
    contentArea.textContent = '';

    if (!currentCountry) {
      const msg = document.createElement('div');
      msg.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
      msg.textContent = 'Select a country to view its timeline';
      contentArea.appendChild(msg);
      return;
    }

    // Header: country name
    const title = document.createElement('h3');
    title.style.cssText = 'margin: 0 0 8px; font-size: 14px; font-weight: 600; color: var(--text-primary);';
    title.textContent = `${currentCountry.name} (${currentCountry.code})`;
    headerArea.appendChild(title);

    const allEvents = getCountryEvents();
    const sorted = allEvents
      .slice()
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Count by type for summary bar
    const typeCounts = new Map<EventType, number>();
    for (const ev of sorted) {
      typeCounts.set(ev.type, (typeCounts.get(ev.type) ?? 0) + 1);
    }

    // Summary bar with counts
    if (typeCounts.size > 0) {
      const summaryBar = document.createElement('div');
      summaryBar.style.cssText = 'display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 6px;';

      const sortedTypes = Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1]);
      for (const [type, count] of sortedTypes) {
        const chip = document.createElement('span');
        chip.style.cssText = `
          font-size: 9px; padding: 2px 6px; border-radius: 10px;
          background: var(--bg-secondary); color: var(--text-secondary);
          border: 1px solid var(--border-primary);
        `;
        chip.textContent = `${TYPE_LABELS[type] ?? type}: ${count}`;
        summaryBar.appendChild(chip);
      }

      headerArea.appendChild(summaryBar);
    }

    // Filter row: checkboxes for event types
    if (typeCounts.size > 1) {
      const filterRow = document.createElement('div');
      filterRow.style.cssText = 'display: flex; gap: 6px; flex-wrap: wrap;';

      for (const [type] of Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1])) {
        const label = document.createElement('label');
        label.style.cssText = 'display: flex; align-items: center; gap: 3px; font-size: 10px; color: var(--text-secondary); cursor: pointer;';

        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.checked = !hiddenTypes.has(type);
        cb.style.cssText = 'margin: 0; width: 12px; height: 12px; cursor: pointer;';
        cb.onchange = () => {
          if (cb.checked) hiddenTypes.delete(type);
          else hiddenTypes.add(type);
          scheduleRender();
        };
        label.appendChild(cb);

        const labelText = document.createElement('span');
        labelText.textContent = TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
        label.appendChild(labelText);

        filterRow.appendChild(label);
      }

      headerArea.appendChild(filterRow);
    }

    // Filter events by hidden types
    const filtered = sorted.filter(e => !hiddenTypes.has(e.type));

    if (filtered.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No events match the selected filters';
      contentArea.appendChild(msg);
      return;
    }

    // Group by time period
    const groups: { label: string; events: NormalizedEvent[] }[] = [];
    let currentGroup: string | null = null;
    let currentGroupEvents: NormalizedEvent[] = [];

    for (const ev of filtered.slice(0, 100)) {
      const group = getTimeGroup(ev.timestamp);
      if (group !== currentGroup) {
        if (currentGroup !== null) {
          groups.push({ label: currentGroup, events: currentGroupEvents });
        }
        currentGroup = group;
        currentGroupEvents = [ev];
      } else {
        currentGroupEvents.push(ev);
      }
    }
    if (currentGroup !== null) {
      groups.push({ label: currentGroup, events: currentGroupEvents });
    }

    // Render timeline
    for (const group of groups) {
      // Time group header
      const groupHeader = document.createElement('div');
      groupHeader.style.cssText = `
        font-size: 11px; font-weight: 600; color: var(--text-secondary);
        margin: 10px 0 6px; padding: 4px 0;
        border-bottom: 1px solid var(--border-primary);
        text-transform: uppercase; letter-spacing: 0.05em;
      `;
      groupHeader.textContent = `${group.label} (${group.events.length})`;
      contentArea.appendChild(groupHeader);

      for (const ev of group.events) {
        const sev = ev.severity ?? 'info';
        const row = document.createElement('div');
        row.style.cssText = `
          display: flex; align-items: flex-start; gap: 8px;
          padding: 6px 4px; margin-bottom: 2px; border-radius: 4px;
          cursor: pointer;
        `;
        row.onmouseenter = () => { row.style.background = 'var(--bg-secondary)'; };
        row.onmouseleave = () => { row.style.background = 'transparent'; };

        row.onclick = () => eventBus.emit('event:selected', ev);
        row.title = 'Click for details';

        // Timestamp column
        const timeCol = document.createElement('div');
        timeCol.style.cssText = 'font-size: 10px; color: var(--text-muted); min-width: 52px; flex-shrink: 0; font-family: var(--font-mono); padding-top: 1px;';
        const ts = new Date(ev.timestamp);
        timeCol.textContent = `${ts.getHours().toString().padStart(2, '0')}:${ts.getMinutes().toString().padStart(2, '0')}`;
        row.appendChild(timeCol);

        // Severity dot
        const dot = document.createElement('div');
        dot.style.cssText = `
          width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; margin-top: 4px;
          background: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
        `;
        row.appendChild(dot);

        // Content column
        const content = document.createElement('div');
        content.style.cssText = 'flex: 1; min-width: 0;';

        // Title + type badge
        const titleRow = document.createElement('div');
        titleRow.style.cssText = 'display: flex; align-items: center; gap: 6px;';

        const titleEl = document.createElement('span');
        titleEl.style.cssText = 'font-size: 12px; color: var(--text-primary); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
        titleEl.textContent = ev.title;
        titleRow.appendChild(titleEl);

        const typeBadge = document.createElement('span');
        typeBadge.style.cssText = `
          font-size: 9px; padding: 1px 4px; border-radius: 3px;
          background: var(--bg-secondary); color: var(--text-muted);
          flex-shrink: 0;
        `;
        typeBadge.textContent = TYPE_LABELS[ev.type] ?? ev.type.replace(/_/g, ' ');
        titleRow.appendChild(typeBadge);

        content.appendChild(titleRow);

        // Source attribution
        const srcEl = document.createElement('div');
        srcEl.style.cssText = 'font-size: 9px; color: var(--text-muted); margin-top: 1px;';
        srcEl.textContent = ev.source;
        content.appendChild(srcEl);

        row.appendChild(content);

        contentArea.appendChild(row);
      }
    }
  }

  render();

  // Listen for country selection
  cleanupFns.push(
    eventBus.on('country:selected', (country) => {
      currentCountry = country;
      hiddenTypes = new Set();
      render();
    }),
  );

  // Re-render when events change
  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
